import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardDescription,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { StringField, TextareaField } from "@/components/shared/FormFields";
import CapitalizationRule from "@/components/shared/CapitalizationRule";
import SpeakerButton from "@/components/ui/SpeakerButton";
import useSpeech from "@/hooks/useSpeech";
import { cn } from "@/utils/utils";

export default function CompleteTheConversationExercise({
  exercise = {},
  onUpdate,
  errors = {},
  isPreview = false,
  showResult = false, // ✅ FIX (parent uses showResult)
  onComplete = () => {},
  setAttempt = () => {},
}) {
  const isEditMode = typeof onUpdate === "function";
  const { speak, stop } = useSpeech();

  /* ================= SAFE QUESTIONS ================= */
  const questions = Array.isArray(exercise?.conversationQuestions)
    ? exercise.conversationQuestions
    : [];

  /* ================= STATE ================= */
  const [answers, setAnswers] = useState({});
  const completedRef = useRef(false);

  /* ================= RESET ON RETRY ================= */
  useEffect(() => {
    if (!exercise?._id) return;

    setAnswers({});
    completedRef.current = false;
  }, [exercise._id]);

  /* ================= RESET FLAG ================= */
  useEffect(() => {
    if (!showResult) {
      completedRef.current = false;
    }
  }, [showResult]);

  /* ================= NORMALIZE ================= */
  const normalize = (text = "", requireCap = false) => {
    let t = String(text || "").trim();
    if (!requireCap) t = t.toLowerCase();
    return t.replace(/[.,!?]/g, "").replace(/\s+/g, " ");
  };

  /* ================= SPEECH ================= */
  const sanitizeForSpeech = (text = "") => {
    return String(text)
      .replace(/_{2,}/g, " blank ")
      .replace(/_/g, " underscore ")
      .replace(/\s+/g, " ")
      .trim();
  };

  const getSpeakerText = (q) => {
    return sanitizeForSpeech(q.question || "");
  };

  /* ================= INPUT ================= */
  const handleInputChange = (idx, value) => {
    if (isEditMode || showResult) return;

    const updated = { ...answers, [idx]: value };

    setAnswers(updated);

    setAttempt({
      answers: updated,
      hasInteracted: true,
    });

    // ✅ IMPORTANT: always notify parent (fix submit button issue)
    onComplete(updated, false);

    const allAnswered = questions.every(
      (_, i) => updated[i]?.trim()
    );

    if (allAnswered && !completedRef.current) {
      completedRef.current = true;
      checkAnswers(updated);
    }
  };

  /* ================= CHECK ================= */
  const checkAnswers = (finalAnswers) => {
    const allCorrect = questions.every((q, idx) => {
      return (
        normalize(finalAnswers[idx], q.requireCapitalization) ===
        normalize(q.answer, q.requireCapitalization)
      );
    });

    // ✅ FIX (CRITICAL)
    onComplete(finalAnswers, allCorrect);
  };

  /* ================= EDIT MODE ================= */
  if (isEditMode) {
    return (
      <div className="space-y-8">
        {questions.map((q, idx) => (
          <Card key={idx} className="p-6 space-y-5">
            <TextareaField
              label={`Line ${idx + 1}`}
              value={q.question || ""}
              onChange={(v) =>
                onUpdate("conversationQuestions", [
                  ...questions.slice(0, idx),
                  { ...q, question: v },
                  ...questions.slice(idx + 1),
                ])
              }
              rows={4}
            />

            <StringField
              label="Correct Answer"
              value={q.answer || ""}
              onChange={(v) =>
                onUpdate("conversationQuestions", [
                  ...questions.slice(0, idx),
                  { ...q, answer: v },
                  ...questions.slice(idx + 1),
                ])
              }
            />

            <CapitalizationRule
              checked={!!q.requireCapitalization}
              onChange={(c) =>
                onUpdate("conversationQuestions", [
                  ...questions.slice(0, idx),
                  { ...q, requireCapitalization: c },
                  ...questions.slice(idx + 1),
                ])
              }
            />

            <Button
              variant="destructive"
              onClick={() =>
                onUpdate(
                  "conversationQuestions",
                  questions.filter((_, i) => i !== idx)
                )
              }
            >
              Remove
            </Button>
          </Card>
        ))}

        <Button
          onClick={() =>
            onUpdate("conversationQuestions", [
              ...questions,
              { question: "", answer: "", requireCapitalization: false },
            ])
          }
        >
          Add Line
        </Button>
      </div>
    );
  }

  /* ================= STUDENT MODE ================= */
  if (!questions.length) {
    return (
      <Card>
        <CardHeader>
          <CardDescription>No questions</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card className="max-w-3xl mx-auto shadow-sm">
      <CardHeader>
        <CardTitle>Complete the Conversation</CardTitle>
        <CardDescription>
          Fill in the missing answers.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-10">
        {questions.map((q, idx) => {
          const userAnswer = answers[idx] || "";

          const isCorrect =
            showResult &&
            normalize(userAnswer, q.requireCapitalization) ===
              normalize(q.answer, q.requireCapitalization);

          return (
            <div
              key={idx}
              className="rounded-xl border bg-muted/40 p-6 space-y-6"
            >
              {/* QUESTION */}
              <pre className="whitespace-pre-wrap text-base">
                {q.question}
              </pre>

              {/* SPEAKER */}
              <div className="flex justify-center">
                <SpeakerButton
                  text={getSpeakerText(q)}
                  onPlay={() => speak(getSpeakerText(q))}
                  onStop={stop}
                />
              </div>

              {/* INPUT */}
              <Input
                value={userAnswer}
                onChange={(e) =>
                  handleInputChange(idx, e.target.value)
                }
                disabled={showResult}
                placeholder="Type answer..."
                className={cn(
                  "text-center text-lg",
                  showResult &&
                    (isCorrect
                      ? "bg-green-100 border-green-400"
                      : "bg-red-100 border-red-400")
                )}
              />

              {/* RESULT TEXT */}
              {showResult && (
                <p
                  className={cn(
                    "text-center font-semibold",
                    isCorrect ? "text-green-600" : "text-red-600"
                  )}
                >
                  {isCorrect
                    ? "Correct ✅"
                    : `Correct answer: ${q.answer}`}
                </p>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
}